import {Component, ElementRef, HostBinding, Input, OnDestroy, OnInit, Optional, Self} from '@angular/core';
import {MatFormFieldControl} from '@angular/material/form-field';
import {Subject} from 'rxjs/internal/Subject';
import {ControlValueAccessor, FormBuilder, FormGroup, NgControl} from '@angular/forms';
import {FocusMonitor} from '@angular/cdk/a11y';
import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {FacadeService} from "../../service-facade/facade.service";

@Component({
  selector: 'tooltip-input',
  templateUrl: './tooltip-input.component.html',
  styleUrls: ['./tooltip-input.component.css'],
  providers: [{provide: MatFormFieldControl, useExisting: TooltipInputComponent}]
})
export class TooltipInputComponent implements MatFormFieldControl<string>, OnDestroy, OnInit, ControlValueAccessor {
  static nextId = 0;
  public toolTip!: string;
  tooltipInputForm: FormGroup;
  stateChanges = new Subject<void>();
  onTouched!: () => void;
  controlType = 'my-input';
  focused = false;
  errorState = false;
  @HostBinding('id') localId = '';
  @HostBinding('attr.aria-describedby') describedBy = '';
  private val!: string;
  private ph!: string;
  private req = false;
  private dis = false;

  /**
   * Constructor
   * @param ngControl
   * @param fb
   * @param fm
   * @param elRef
   */
  constructor(
    @Optional() @Self() public ngControl: NgControl,
    fb: FormBuilder, private fm: FocusMonitor, public facadeService : FacadeService,
    private elRef: ElementRef<HTMLElement>) {
    this.tooltipInputForm = fb.group({
      data: ['', this.required]
    });

    if (ngControl != null) {
      ngControl.valueAccessor = this;
    }
    fm.monitor(elRef.nativeElement, true).subscribe(origin => {
      this.focused = !!origin;
      this.stateChanges.next();
    });

    this.errorState = this.tooltipInputForm == null ? false : this.tooltipInputForm.invalid;
  }

  /**
   * Initialize the class.
   */
  ngOnInit(): void {
   let watchItem = this.facadeService.watchStorageItem(this.ngControl.name + "-tooltip");
   if (watchItem != undefined) {
     watchItem.subscribe(setting => {
       this.toolTip = setting;
     });
   }
  }

  /**
   * returns true if the tooltip should not display
   */
  public get isTooltipDisabled(): boolean {
    if (this.toolTip == null || this.toolTip == undefined)
    {
      return true;
    }
    return false;
  }

  /**
   * Get the value of the input box
   */
  public get id(): string {
    return this.localId;
  }

  /**
   * Set the value in the input box
   * @param val New value to set.
   */
  public set id(val: string) {
    this.localId = val;
    this.stateChanges.next();
  }

  /**
   * Get the value of the input box
   */
  public get value(): string {
    return this.val;
  }

  /**
   * Set the value in the input box
   * @param val New value to set.
   */
  public set value(val: string) {
    this.val = val;
    this.stateChanges.next();
  }

  /**
   * Get the placeholder text in the input box.
   */
  @Input()
  get placeholder() {
    return this.ph;
  }

  /**
   * Set the placeholder text in the input box.
   * @param plh
   */
  set placeholder(plh) {
    this.ph = plh;
    this.stateChanges.next();
  }

  /**
   * Check if the input box is empty.
   */
  get empty() {
    if (this.value == null || this.value.length == 0) {
      return true;
    }
    return false;
  }

  /**
   * Get if the label should be floating
   */
  @HostBinding('class.floating')
  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  /**
   * Check if the field should be required.
   */
  @Input()
  get required() {
    return this.req;
  }

  /**
   * Set the field to required.
   * @param req True if the field is required.
   */
  set required(req) {
    this.req = coerceBooleanProperty(req);
    this.stateChanges.next();
  }

  /**
   * Get if the field is disabled.
   */
  @Input()
  get disabled(): boolean {
    return this.dis;
  }

  set disabled(value: boolean) {
    this.dis = coerceBooleanProperty(value);
    this.stateChanges.next();
  }

  onChange = (_: any) => {
  };

  setDescribedByIds(ids: string[]) {
    this.describedBy = ids.join(' ');
  }

  onContainerClick(event: MouseEvent) {
    if ((event.target as Element).tagName.toLowerCase() !== 'input') {
      this.elRef.nativeElement.querySelector('input')?.focus();
    }
    this.onTouched();
  }

  ngOnDestroy() {
    this.stateChanges.complete();
    this.fm.stopMonitoring(this.elRef.nativeElement);
  }

  writeValue(value: any): void {
    this.tooltipInputForm?.get('data')?.setValue(value);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.tooltipInputForm?.get('data')?.disable() : this.tooltipInputForm?.get('data')?.enable();
  }

  input() {
    this.value = this.tooltipInputForm.get('data')?.value;

    this.onChange(this.value);
  }
}
